import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import { JarvisTheme } from "../../theme/jarvisTheme";
import firebase, { db } from "../../firebase";
import "firebase/compat/storage";

type ResumeVersionDoc = {
  filename: string;
  storagePath: string;
  createdAt: firebase.firestore.Timestamp | null;
  sizeBytes: number;
  activePublishedAt?: firebase.firestore.Timestamp | null;
};

type ResumeConfigDoc = {
  activeVersionId: string | null;
  activeStoragePath: string;
  activeDownloadUrl: string | null;
  updatedAt: firebase.firestore.Timestamp | null;
};

const OWNER_EMAIL = "ayush@acchaudhary.com";
const ACTIVE_STORAGE_PATH = "resumes/active.pdf";

function isProbablyPdf(file: File): boolean {
  if (file.type === "application/pdf") return true;
  return file.name.toLowerCase().endsWith(".pdf");
}

function sanitizeFileName(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9._-]/g, "");
}

function fmtBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function fmtDate(ts: firebase.firestore.Timestamp | null | undefined): string {
  if (!ts) return "—";
  try {
    return ts.toDate().toLocaleString();
  } catch {
    return "—";
  }
}

function ownerGuardLabel(): string {
  return `Owner access required (${OWNER_EMAIL}).`;
}

async function publishActiveFromFile(file: File): Promise<string> {
  const storage = firebase.storage();
  const activeRef = storage.ref(ACTIVE_STORAGE_PATH);
  await activeRef.put(file, { contentType: "application/pdf" });
  return await activeRef.getDownloadURL();
}

async function publishActiveFromPrivatePath(privatePath: string): Promise<string> {
  const storage = firebase.storage();
  const srcRef = storage.ref(privatePath);
  return await srcRef.getDownloadURL();
}

async function upsertResumeConfig(params: {
  activeVersionId: string;
  activeDownloadUrl: string;
  activeStoragePath?: string;
}): Promise<void> {
  await db
    .collection("site_config")
    .doc("resume")
    .set(
      {
        activeVersionId: params.activeVersionId,
        activeStoragePath: params.activeStoragePath ?? ACTIVE_STORAGE_PATH,
        activeDownloadUrl: params.activeDownloadUrl,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      } satisfies Omit<ResumeConfigDoc, "updatedAt"> & { updatedAt: any },
      { merge: true },
    );
}

async function pruneOldVersions(params: { activeVersionId: string | null }): Promise<void> {
  const snap = await db
    .collection("resume_versions")
    .orderBy("createdAt", "desc")
    .limit(50)
    .get();

  const versions = snap.docs.map((d) => ({
    id: d.id,
    data: d.data() as ResumeVersionDoc,
  }));

  const keep: typeof versions = [];
  const nonActive: typeof versions = [];
  for (const v of versions) {
    if (params.activeVersionId && v.id === params.activeVersionId) keep.push(v);
    else nonActive.push(v);
  }

  // Keep active + 9 newest non-active
  const keepNonActive = nonActive.slice(0, 9);
  const toDelete = nonActive.slice(9);

  const storage = firebase.storage();
  await Promise.all(
    toDelete.map(async (v) => {
      const path = v.data.storagePath;
      try {
        if (typeof path === "string" && path.startsWith("resumes/versions/")) {
          await storage.ref(path).delete();
        }
      } catch {
        // ignore missing objects
      }
      await db.collection("resume_versions").doc(v.id).delete();
    }),
  );

  // Ensure we didn't keep > 10 due to duplicates/etc; in that case, do nothing further.
  void keepNonActive;
  void keep;
}

export function ResumeTab() {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [makeActiveAfterUpload, setMakeActiveAfterUpload] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  const [config, setConfig] = useState<ResumeConfigDoc | null>(null);
  const [versions, setVersions] = useState<Array<{ id: string; data: ResumeVersionDoc }>>([]);

  const didInitialLoad = useRef(false);

  const refresh = useCallback(async () => {
    setOk(null);
    setError(null);
    setLoading(true);
    try {
      const cfgSnap = await db.collection("site_config").doc("resume").get();
      setConfig(
        cfgSnap.exists
          ? ({
              activeVersionId: (cfgSnap.data()?.activeVersionId as string | null) ?? null,
              activeStoragePath:
                (cfgSnap.data()?.activeStoragePath as string | undefined) ??
                ACTIVE_STORAGE_PATH,
              activeDownloadUrl:
                (cfgSnap.data()?.activeDownloadUrl as string | null) ?? null,
              updatedAt:
                (cfgSnap.data()?.updatedAt as firebase.firestore.Timestamp | null) ?? null,
            } satisfies ResumeConfigDoc)
          : null,
      );

      const vSnap = await db
        .collection("resume_versions")
        .orderBy("createdAt", "desc")
        .limit(25)
        .get();

      const v = vSnap.docs.map((d) => ({ id: d.id, data: d.data() as ResumeVersionDoc }));
      setVersions(v);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load resume config.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (didInitialLoad.current) return;
    didInitialLoad.current = true;
    void refresh();
  }, [refresh]);

  const activeVersionId = config?.activeVersionId ?? null;

  const displayVersions = useMemo(() => {
    if (versions.length === 0) return [];
    const active = activeVersionId
      ? versions.filter((v) => v.id === activeVersionId)
      : [];
    const nonActive = versions.filter((v) => v.id !== activeVersionId);
    return [...active, ...nonActive].slice(0, 10);
  }, [versions, activeVersionId]);

  const onUpload = useCallback(async () => {
    if (!selectedFile) return;
    setOk(null);
    setError(null);

    if (!isProbablyPdf(selectedFile)) {
      setError("Please upload a PDF file.");
      return;
    }

    setBusy(true);
    setUploadPct(0);
    try {
      const versionDocRef = db.collection("resume_versions").doc();
      const id = versionDocRef.id;
      const safeName = sanitizeFileName(selectedFile.name) || "resume.pdf";
      const privatePath = `resumes/versions/${id}_${safeName}`;

      const storage = firebase.storage();
      const privateRef = storage.ref(privatePath);
      const task = privateRef.put(selectedFile, { contentType: "application/pdf" });

      await new Promise<void>((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            if (!snap.totalBytes) return;
            const pct = (snap.bytesTransferred / snap.totalBytes) * 100;
            setUploadPct(Number.isFinite(pct) ? pct : null);
          },
          (err) => reject(err),
          () => resolve(),
        );
      });

      await versionDocRef.set({
        filename: selectedFile.name,
        storagePath: privatePath,
        sizeBytes: selectedFile.size,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      } satisfies Omit<ResumeVersionDoc, "createdAt"> & { createdAt: any });

      let publishedUrl: string | null = null;
      if (makeActiveAfterUpload) {
        publishedUrl = await publishActiveFromFile(selectedFile);

        await upsertResumeConfig({ activeVersionId: id, activeDownloadUrl: publishedUrl });

        await db.collection("resume_versions").doc(id).set(
          {
            activePublishedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );
      }

      await pruneOldVersions({ activeVersionId: makeActiveAfterUpload ? id : activeVersionId });

      setSelectedFile(null);
      setUploadPct(null);
      setOk(
        makeActiveAfterUpload
          ? "Uploaded and published as active resume."
          : "Uploaded as a private version (not active).",
      );
      await refresh();
      void publishedUrl;
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : `Upload failed. ${ownerGuardLabel()}`,
      );
    } finally {
      setBusy(false);
      setUploadPct(null);
    }
  }, [selectedFile, makeActiveAfterUpload, refresh, activeVersionId]);

  const setActive = useCallback(
    async (versionId: string) => {
      setOk(null);
      setError(null);
      setBusy(true);
      try {
        const doc = await db.collection("resume_versions").doc(versionId).get();
        if (!doc.exists) throw new Error("Version not found.");
        const data = doc.data() as ResumeVersionDoc;
        if (!data?.storagePath) throw new Error("Version is missing storagePath.");

        const activeUrl = await publishActiveFromPrivatePath(data.storagePath);
        await upsertResumeConfig({
          activeVersionId: versionId,
          activeStoragePath: data.storagePath,
          activeDownloadUrl: activeUrl,
        });
        await db.collection("resume_versions").doc(versionId).set(
          {
            activePublishedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );

        await pruneOldVersions({ activeVersionId: versionId });
        setOk("Active resume updated.");
        await refresh();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : `Failed to set active. ${ownerGuardLabel()}`);
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  const deleteVersion = useCallback(
    async (versionId: string) => {
      if (versionId === activeVersionId) return;
      setOk(null);
      setError(null);
      setBusy(true);
      try {
        const doc = await db.collection("resume_versions").doc(versionId).get();
        if (doc.exists) {
          const data = doc.data() as ResumeVersionDoc;
          const path = data?.storagePath;
          if (typeof path === "string" && path.startsWith("resumes/versions/")) {
            try {
              await firebase.storage().ref(path).delete();
            } catch {
              // ignore missing object
            }
          }
        }
        await db.collection("resume_versions").doc(versionId).delete();
        await pruneOldVersions({ activeVersionId });
        setOk("Deleted version.");
        await refresh();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : `Failed to delete. ${ownerGuardLabel()}`);
      } finally {
        setBusy(false);
      }
    },
    [refresh, activeVersionId],
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontFamily: JarvisTheme.fonts.ui,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontSize: "0.8rem",
            color: JarvisTheme.colors.muted,
            mb: 0.5,
            opacity: 0.85,
          }}
        >
          Resume
        </Typography>
        <Typography sx={{ opacity: 0.72, fontSize: "0.85rem", color: JarvisTheme.colors.muted }}>
          Manage resume versions (max 10). Publish updates instantly to <code>/resume</code>.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {ok}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 1,
          border: `1px solid ${JarvisTheme.colors.panelBorder}`,
          bgcolor: JarvisTheme.colors.panelSoft,
          color: JarvisTheme.colors.text,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: JarvisTheme.fonts.mono, fontSize: "0.85rem" }}>
              Active version:{" "}
              <strong>{activeVersionId ?? "—"}</strong>
            </Typography>
            <Typography sx={{ fontFamily: JarvisTheme.fonts.mono, fontSize: "0.75rem", opacity: 0.7 }}>
              Updated: {fmtDate(config?.updatedAt ?? null)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refresh()}
              disabled={loading || busy}
              sx={{
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                borderColor: JarvisTheme.colors.accentBorder,
                color: JarvisTheme.colors.muted,
                "&:hover": { borderColor: JarvisTheme.colors.accent },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {config?.activeDownloadUrl && (
          <Typography
            sx={{
              mt: 1.25,
              fontFamily: JarvisTheme.fonts.mono,
              fontSize: "0.75rem",
              opacity: 0.72,
              overflowWrap: "anywhere",
            }}
          >
            Active URL: {config.activeDownloadUrl}
          </Typography>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 1,
          border: `1px solid ${JarvisTheme.colors.panelBorder}`,
          bgcolor: JarvisTheme.colors.panelSoft,
          color: JarvisTheme.colors.text,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: JarvisTheme.fonts.ui,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontSize: "0.75rem",
            color: JarvisTheme.colors.muted,
            mb: 1,
          }}
        >
          Upload new version
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <input
            type="file"
            accept="application/pdf,.pdf"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setSelectedFile(f);
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={makeActiveAfterUpload}
                onChange={(e) => setMakeActiveAfterUpload(e.target.checked)}
                disabled={busy}
                sx={{
                  color: JarvisTheme.colors.muted,
                  "&.Mui-checked": { color: JarvisTheme.colors.accent },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.9rem" }}>
                Make active after upload
              </Typography>
            }
          />

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Button
              variant="contained"
              disabled={!selectedFile || busy}
              onClick={() => void onUpload()}
              sx={{
                fontFamily: JarvisTheme.fonts.ui,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                bgcolor: JarvisTheme.colors.accent,
                color: JarvisTheme.colors.bg0,
                "&:hover": { bgcolor: "rgba(88, 230, 255, 0.85)" },
              }}
            >
              Upload
            </Button>

            {busy && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: JarvisTheme.colors.accent }} />
                <Typography sx={{ fontSize: "0.85rem", opacity: 0.75 }}>
                  Working…
                </Typography>
              </Box>
            )}
          </Box>

          {typeof uploadPct === "number" && (
            <Box>
              <LinearProgress
                variant="determinate"
                value={uploadPct}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  bgcolor: JarvisTheme.colors.accentTrack,
                  "& .MuiLinearProgress-bar": { bgcolor: JarvisTheme.colors.accent },
                }}
              />
              <Typography sx={{ mt: 0.5, opacity: 0.7, fontSize: "0.75rem" }}>
                {uploadPct.toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Divider sx={{ borderColor: JarvisTheme.colors.panelBorder, mb: 2 }} />

      <Typography
        sx={{
          fontFamily: JarvisTheme.fonts.ui,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          fontSize: "0.75rem",
          color: JarvisTheme.colors.muted,
          mb: 1,
        }}
      >
        Versions (showing up to 10)
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={22} sx={{ color: JarvisTheme.colors.accent }} />
        </Box>
      ) : displayVersions.length === 0 ? (
        <Alert severity="info">No resume versions found yet. Upload one above.</Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {displayVersions.map((v) => {
            const isActive = v.id === activeVersionId;
            return (
              <Paper
                key={v.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${JarvisTheme.colors.panelBorder}`,
                  bgcolor: JarvisTheme.colors.panelSoft,
                  color: JarvisTheme.colors.text,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: JarvisTheme.fonts.mono,
                        fontSize: "0.85rem",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {isActive ? "ACTIVE · " : ""}
                      {v.data.filename}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: JarvisTheme.fonts.mono,
                        fontSize: "0.72rem",
                        opacity: 0.72,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {v.id} · {fmtBytes(v.data.sizeBytes)} · Uploaded: {fmtDate(v.data.createdAt)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {!isActive && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => void setActive(v.id)}
                        sx={{
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          borderColor: JarvisTheme.colors.accentBorder,
                          color: JarvisTheme.colors.muted,
                          "&:hover": { borderColor: JarvisTheme.colors.accent },
                        }}
                      >
                        Set active
                      </Button>
                    )}
                    {!isActive && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => void deleteVersion(v.id)}
                        sx={{
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          borderColor: "rgba(255, 120, 120, 0.35)",
                          color: "rgba(255, 200, 200, 0.8)",
                          "&:hover": { borderColor: "rgba(255, 120, 120, 0.6)" },
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

